/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */
import { IBoxModel } from "@adapptio/lib-shared";

export type TDndID = string;

export type TDndDropAreaID = TDndID;

export type TDndDraggableID = TDndID;

export interface IDndDropAreaProps {}

export interface IDndDraggableElProps {}

export enum DRAG_PHASE_TYPE {
	IDLE = "idle",
	PENDING = "pending",
	DRAGGING = "dragging"
}

export interface IDndDragPoint {
	x: number;
	y: number;
}

export interface IDndDragPhase {
	type: DRAG_PHASE_TYPE;
	draggableId: TDndDraggableID;
	dragging?: IDndDraggableInfo;
	draggingOver: TDndDropAreaID;
	initialPoint: IDndDragPoint;
}

export interface IDndDragEventBinding<T extends keyof WindowEventMap = keyof WindowEventMap> {
	eventName: T;
	handler: (event: WindowEventMap[T]) => void;
}

export enum DND_STATE_PHASE {
	IDLE = "idle",
	PRE_DRAG = "pre_drag",
	DRAGGING = "dragging"
}

export interface IDndDraggableDimensions {
	width: number;
	height: number;
}

export interface IDndState {
	phase: DND_STATE_PHASE;
	isDragging: boolean;
	dimensions: Record<string, IDndDraggableDimensions>;
	draggingOver: TDndDropAreaID;
}

export enum DND_ACTION {
	PREPARE_DRAG = "prepareDrag",
	DRAG = "drag"
}

interface IDndActionBase {
	type: DND_ACTION;
}

interface IDndActionPreDrag extends IDndActionBase {
	type: DND_ACTION.PREPARE_DRAG;
	draggableId: TDndDraggableID;
	point: IDndDragPoint;
}

export type TDndAction = IDndActionPreDrag;

export interface IDndDraggableRegistryEntry {
	id: string;
	index: number;
	droppableId: TDndDropAreaID;
	getInfo: () => IDndDraggableInfo;
	onDragMove: (data: IDndDragMoveEventData) => void;
	onDragDrop: () => void;
}

export interface IDndDraggableRegistryApi {
	register: (entry: IDndDraggableRegistryEntry) => void;
	unregister: (id: TDndDraggableID) => void;
	exists: (id: TDndDraggableID) => boolean;
	findById: (id: TDndDraggableID) => IDndDraggableRegistryEntry | null;
}

export interface IDndDropAreaRegistryEntry {
	id: string;
	onDragMove: (data: IDndDragMoveEventData) => void;
	onDragDrop: () => void;
}

export interface IDndDropAreaRegistryApi {
	register: (entry: IDndDropAreaRegistryEntry) => void;
	unregister: (id: TDndDropAreaID) => void;
	exists: (id: TDndDropAreaID) => boolean;
	findById: (id: TDndDropAreaID) => IDndDropAreaRegistryEntry | null;
}

export interface IDndRegistry {
	draggable: IDndDraggableRegistryApi;
	droppable: IDndDropAreaRegistryApi;
	getDraggablesOfDropArea: (droppableId: TDndDropAreaID) => IDndDraggableRegistryEntry[];
	emitDragMove: (data: IDndDragMoveEventData) => void;
	emitDrop: () => void;
	clean: () => void;
}

export interface IDndRegistryEntries {
	draggables: Record<TDndDraggableID, IDndDraggableRegistryEntry>;
	droppables: Record<TDndDropAreaID, IDndDropAreaRegistryEntry>;
}

export interface IDndDraggableInfo {
	draggableId: TDndDraggableID;
	tagName: string;
	box: IBoxModel;
	display: React.CSSProperties["display"];
}

export interface IDndDragMoveEventData {
	dragging: IDndDraggableInfo;
	initialPoint: IDndDragPoint;
	currentPoint: IDndDragPoint;
	draggingOver: TDndDropAreaID;
}

export interface IDndDragEndData {
	destination: {
		droppableId: TDndDropAreaID;
		index: number;
	};
	source: {
		droppableId: TDndDropAreaID;
		draggableId: TDndDraggableID;
		index: number;
	};
}
